import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useQuote } from "@/context/QuoteContext";
import { Reveal } from "@/components/Reveal";

export default function IntroHero() {
  const { openQuoteModal } = useQuote();

  return (
    <section className="relative pt-20 pb-4 sm:pt-24 sm:pb-6 lg:pt-28 lg:pb-8 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10 bg-white" />
      <div className="absolute inset-0 -z-10 bg-grid opacity-70" />
      <div className="absolute -top-32 -right-32 -z-10 h-[520px] w-[520px] rounded-full bg-medis-teal/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 -z-10 h-[420px] w-[420px] rounded-full bg-medis-blue/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-10 lg:gap-14 lg:items-center">
          <div className="order-2 lg:order-2 lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-medis-blue/15 via-medis-teal/10 to-transparent blur-xl" />
              <div className="relative overflow-hidden rounded-xl border border-medis-silver/40 shadow-[0_20px_60px_-30px_rgba(11,60,130,0.35)] bg-white">
                <motion.img
                  alt="MRI Scanner in a clinical setting"
                  initial={{ scale: 1.06 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-[380px] sm:h-[460px] object-cover"
                  data-testid="hero-image"
                  src="/hero.jpg"
                />
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-medis-navy/70 to-transparent" />
                <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between text-white">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.16em] opacity-80">Featured Capability</div>
                    <div className="font-display text-lg font-semibold">MRI · CT · Cath Lab</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10.5px] uppercase tracking-[0.16em] opacity-80">Pan-India</div>
                    <div className="font-display text-lg font-semibold">Deployed</div>
                  </div>
                </div>
              </div>
              <div className="absolute -left-3 top-8 hidden sm:block" style={{ animation: "float-pill 5s ease-in-out infinite" }}>
                <div className="rounded-md border border-medis-silver/40 bg-white/95 backdrop-blur px-4 py-3 shadow-lg hover:shadow-xl hover:scale-105 transition-[box-shadow,transform] duration-200 cursor-default">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-medis-gray">Uptime Focus</div>
                  <div className="mt-1 font-display font-bold text-medis-blue text-xl">Lifecycle Ready</div>
                </div>
              </div>
              <div className="absolute -right-3 -bottom-4 hidden sm:block" style={{ animation: "float-pill 5s ease-in-out 1.2s infinite" }}>
                <div className="rounded-md border border-medis-silver/40 bg-white/95 backdrop-blur px-4 py-3 shadow-lg hover:shadow-xl hover:scale-105 transition-[box-shadow,transform] duration-200 cursor-default">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-medis-green animate-pulse" />
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-medis-gray">Support Live</div>
                  </div>
                  <div className="mt-1 font-display font-bold text-medis-navy text-lg">Engineers On-Call</div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:hidden">
            <div
              className="inline-flex items-center gap-2.5 rounded-full border border-medis-blue/15 bg-medis-blue/[0.04] px-5 py-2 text-[13px] uppercase tracking-[0.16em] text-medis-blue font-semibold"
              data-testid="hero-eyebrow-mobile"
            >
              <span className="h-2 w-2 rounded-full bg-medis-green" />
              Trusted Partner in Imaging & Medical Equipment
            </div>
          </div>

          <div className="order-3 lg:order-1 lg:col-span-7">
            <Reveal>
              <div
                className="hidden lg:inline-flex items-center gap-2.5 rounded-full border border-medis-blue/15 bg-medis-blue/[0.04] px-5 py-2 text-[13px] uppercase tracking-[0.16em] text-medis-blue font-semibold"
                data-testid="hero-eyebrow"
              >
                <span className="h-2 w-2 rounded-full bg-medis-green" />
                Trusted Partner in Imaging & Medical Equipment
              </div>
              <h1
                className="mt-6 font-display font-bold text-medis-navy text-[28px] sm:text-[34px] lg:text-[42px] leading-[1.1] tracking-tight"
                data-testid="hero-headline"
              >
                Advanced <span className="text-medis-blue">MRI</span>, <span className="text-medis-blue">CT</span> &{" "}
                <span className="text-medis-blue">Cath Lab</span> Solutions for Modern Healthcare
              </h1>
              <p className="mt-6 text-medis-gray text-[16.5px] sm:text-lg leading-relaxed max-w-2xl" data-testid="hero-subheadline">
                MEDIS Group LLP supplies diagnostic imaging systems and critical support solutions for hospitals and
                diagnostic centres across India backed by installation, technical service, spare parts, helium,
                cryogenic expertise and long-term maintenance support.
              </p>
              <p className="mt-4 text-medis-gray text-[16.5px] sm:text-lg leading-relaxed max-w-2xl">
                We work with hospitals and diagnostic centres across India to ensure imaging systems stay operational
                beyond the point of supply through technical support, spare parts availability, helium services and
                structured AMC/CMC programmes.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => openQuoteModal()}
                  className="group inline-flex items-center gap-2 bg-medis-blue text-white px-6 py-3.5 rounded-md text-[14.5px] font-medium hover:bg-medis-blue-dark transition-colors"
                  data-testid="hero-cta-quote-secondary"
                >
                  Get a Quote
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href="#products"
                  className="inline-flex items-center gap-2 border border-medis-navy/15 text-medis-navy px-6 py-3.5 rounded-md text-[14.5px] font-medium hover:border-medis-teal hover:text-medis-blue transition-colors"
                  data-testid="hero-cta-explore-secondary"
                >
                  Explore Products
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
